/**
 * Contact Card Component
 * 
 * Displays contact information in a card format with photo, details, and action buttons.
 * 
 * Key Features:
 * - Contact detail navigation (click to open contact page)
 * - Notes navigation (notes button in bottom action bar)
 * - AI summarization (chat button in bottom action bar requests server-side contact note analysis)
 * - Drag-to-chat integration: Drag contact cards to chat panel to analyze contact notes
 * - Delete contact functionality (X button in top-right corner)
 * - Keyword highlighting for search results
 * - Contact photo display with fallback
 * - Type chip display (RLP/Team classification) in bottom action bar
 * - Light background with dark mode support
 * 
 * Layout:
 * - Header: Delete (X) button in top-right corner
 * - Content: Contact photo and details in main area with proper spacing to avoid overlap
 * - Footer: Tags and action buttons aligned to left with border separator
 * 
 * Drag-to-Chat Implementation:
 * - Cards are draggable only when contact has notes (noteCount > 0)
 * - Drag transfers only a record reference and reveals Chat; only a completed Chat drop submits
 * - Visual feedback during drag (opacity change)
 * 
 * Props:
 * - contact: Contact object with contact details and noteCount
 * - keywords: Array of strings for highlighting search matches
 * - onDelete: Callback function for contact deletion
 */

import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { Contact } from '../../models/Contact';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { getContactPhotoUrl } from '../../utils/avatarUtils';
import { useEventBus } from '../../context/EventBusContext';
import { EVENTS } from '../../constants/eventConstants';
import { getCardAnalysisPrompt } from '../../utils/summaryUtils';
import { highlightText } from '../../utils/highlightUtils';
import { useTeams } from '../../context/TeamContext';
import { getContactCardDisplayFields, getContactFieldValue } from '../../utils/contactUtils';
import { renderTypeChip } from '../../utils/typeChipUtils';
import { canEdit, canDelete } from '../../utils/permissions';
import { Avatar } from '../primitives';

interface ContactCardProps {
  contact: Contact;
  keywords: string[];
  onDelete: (id: number) => Promise<void>;
  showDeleteButton?: boolean; // Override permission checks for delete button visibility
}

const ContactCard: FC<ContactCardProps> = ({ contact, keywords, onDelete, showDeleteButton }) => {
  const navigate = useNavigate();
  const { publish } = useEventBus();
  const { currentUserId, teams } = useTeams();
  const [isDragging, setIsDragging] = useState(false);

  // Check permissions
  const hasEditPermission = canEdit(contact, teams, currentUserId);
  const hasDeletePermission = canDelete(contact, currentUserId);

  // Determine if delete button should be shown (either explicitly set or based on permissions)
  const shouldShowDeleteButton = showDeleteButton ?? hasDeletePermission;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await onDelete(contact.id);
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/contacts/${contact.id}/notes`);
  };

  const sendNotesToChat = () => {
    publish(EVENTS.CHAT_OPEN, {
      prompt: getCardAnalysisPrompt({ contactId: contact.id }),
      createNewSession: true,
    });
  };

  const handleSummarize = (e: React.MouseEvent) => {
    e.stopPropagation();
    sendNotesToChat();
  };

  // Reveal Chat on drag intent; the Chat drop target owns submission.
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (contact.noteCount === 0) {
      e.preventDefault();
      return;
    }
    // Set dragging state for visual feedback
    setIsDragging(true);
    e.dataTransfer.setData('application/json', JSON.stringify({ contactId: contact.id }));
    e.dataTransfer.effectAllowed = 'copy';
    publish(EVENTS.CHAT_OPEN_FOR_DROP, {});
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      className={`p-4 rounded-lg ${THEME_CLASSES.card.background} border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.hover.full} relative min-h-[200px] flex flex-col ${isDragging ? 'opacity-50' : ''}`}
      draggable={contact.noteCount > 0}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      title={contact.noteCount > 0 ? 'Drag to chat to analyze notes' : 'No notes to analyze'}
    >
      {/* Delete button and permission indicator in top-right corner */}
      <div className="flex justify-end items-center gap-1 absolute top-2 right-2">
        {/* Permission indicator */}
        {!hasEditPermission && (
          <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" role="img" aria-label="View-only access">
            <path fillRule="evenodd" d="M10 12a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
        )}
        {/* Delete button - show based on prop or permission check */}
        {shouldShowDeleteButton && (
          <button
            onClick={handleDelete}
            className={`${THEME_CLASSES.button.iconSmall}`}
            title="Remove contact"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      <div
        className="flex items-stretch gap-4 cursor-pointer flex-grow pr-8"
        onClick={() => navigate(`/contacts/${contact.id}`)}
      >
        <div className="flex-shrink-0 self-stretch">
          <Avatar
            src={getContactPhotoUrl(contact)}
            name={contact.displayName}
            size="lg"
            shape="rounded"
            className={`h-full w-24 border-2 ${THEME_CLASSES.card.border}`}
          />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <h3 className={`text-base font-semibold ${THEME_CLASSES.text.primary} break-words`}>
            {keywords.length ? highlightText(contact.displayName, keywords) : contact.displayName}
          </h3>

          <div className="text-xs space-y-2">
            {getContactCardDisplayFields().map(field => {
              const value = getContactFieldValue(contact, field);
              if (field.hideEmpty && !value) {
                return null;
              }

              const displayValue = value ?? '-';

              return (
                <div key={field.key} className="flex justify-between items-start gap-4">
                  <span className={`${THEME_CLASSES.text.secondary} shrink-0`}>{field.label}:</span>
                  <span className={`${THEME_CLASSES.text.secondary} text-right`}>
                    {keywords.length && displayValue !== '-'
                      ? highlightText(displayValue.toString(), keywords)
                      : displayValue}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom section with tags left and actions right */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          {renderTypeChip({
            rlpCompanyId: 0,
            teamId: contact.teamId,
            rlpPersonId: contact.rlpPersonId ? Number(contact.rlpPersonId) : undefined
          })}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNotesClick}
            className={`${THEME_CLASSES.button.iconSmall}`}
            title="View notes"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </button>
          <button
            onClick={handleSummarize}
            className={`${THEME_CLASSES.button.iconSmall}`}
            title="Ask AI about this contact"
          >
            <svg className={`w-4 h-4 ${THEME_CLASSES.text.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactCard;
